/**
 * API Cache Utility
 * 
 * Provides centralized caching for API responses to reduce redundant network requests
 * Supports time-based expiration, forced invalidation, and persistence between sessions
 */

type CacheEntry<T> = {
  data: T;
  timestamp: number;
  expiry: number | null; // null means no expiry
};

class ApiCache {
  private cache: Record<string, CacheEntry<any>> = {};
  private defaultExpiry = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
  private storageKey = 'api_cache_v1';
  private isInitialized = false;

  constructor() {
    this.loadFromStorage();
    
    // Set up event listener for storage changes from other tabs
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageChange);
    }
  }

  /**
   * Handle storage changes from other tabs/windows
   */
  private handleStorageChange = (event: StorageEvent): void => {
    if (event.key === this.storageKey) {
      console.log('[API-CACHE] Cache updated in another tab, reloading');
      this.loadFromStorage();
    }
  };

  /**
   * Load cache from localStorage
   */
  private loadFromStorage(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const savedCache = localStorage.getItem(this.storageKey);
      if (savedCache) {
        const parsedCache = JSON.parse(savedCache);
        // Validate the structure
        if (typeof parsedCache === 'object') {
          this.cache = parsedCache;
          console.log(`[API-CACHE] Loaded ${Object.keys(this.cache).length} entries from storage`);
          this.isInitialized = true;
        }
      }
    } catch (err) {
      console.error('[API-CACHE] Error loading from localStorage:', err);
      // If load fails, reset to empty cache
      this.cache = {};
    }
  }

  /**
   * Save cache to localStorage
   */
  private saveToStorage(): void {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.cache));
    } catch (err) {
      console.error('[API-CACHE] Error saving to localStorage:', err);
      
      // If storage is full, try to clear expired entries and retry
      if (err instanceof DOMException && (
        err.name === 'QuotaExceededError' ||
        err.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      )) {
        this.clearExpiredEntries();
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(this.cache));
        } catch (retryErr) {
          console.error('[API-CACHE] Still cannot save after clearing expired entries');
        }
      }
    }
  }

  /**
   * Clear expired entries to free up space
   */
  private clearExpiredEntries(): void {
    const now = Date.now();
    let clearedCount = 0;
    
    Object.keys(this.cache).forEach(key => {
      const entry = this.cache[key];
      if (entry.expiry && now > entry.timestamp + entry.expiry) {
        delete this.cache[key];
        clearedCount++;
      }
    });
    
    console.log(`[API-CACHE] Cleared ${clearedCount} expired entries`);
  }

  /**
   * Get data from cache if it exists and is valid
   */
  get<T>(key: string): T | null {
    // Ensure cache is loaded
    if (!this.isInitialized) {
      this.loadFromStorage();
    }
    
    const entry = this.cache[key];
    
    if (!entry) return null;
    
    // Check if cache has expired
    if (entry.expiry !== null) {
      const now = Date.now();
      if (now > entry.timestamp + entry.expiry) {
        console.log(`[API-CACHE] Cache expired for: ${key}`);
        delete this.cache[key];
        this.saveToStorage();
        return null;
      }
    }
    
    console.log(`[API-CACHE] Cache hit for: ${key}`);
    return entry.data;
  }

  /**
   * Store data in cache with optional expiry time
   */
  set<T>(key: string, data: T, expiry: number | null = this.defaultExpiry): void {
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      expiry
    };
    console.log(`[API-CACHE] Stored in cache: ${key}, expires in: ${expiry ? this.formatTime(expiry) : 'never'}`);
    
    // Save to localStorage after updating
    this.saveToStorage();
  }

  /**
   * Clear a specific item from cache
   */
  clear(key: string): void {
    if (this.cache[key]) {
      delete this.cache[key];
      console.log(`[API-CACHE] Cleared from cache: ${key}`);
      
      // Save to localStorage after updating
      this.saveToStorage();
    }
  }

  /**
   * Clear all items from cache
   */
  clearAll(): void {
    this.cache = {};
    console.log('[API-CACHE] Cache cleared completely');
    
    // Save to localStorage after updating
    this.saveToStorage();
  }

  /**
   * Format milliseconds into a human-readable time string
   */
  private formatTime(ms: number): string {
    if (ms >= 3600000) {
      return `${(ms / 3600000).toFixed(1)} hours`;
    } else if (ms >= 60000) {
      return `${(ms / 60000).toFixed(1)} minutes`;
    } else {
      return `${(ms / 1000).toFixed(1)} seconds`;
    }
  }
  
  /**
   * Get cache status information
   */
  getStatus(): { 
    entries: number, 
    keys: string[], 
    sizes: Record<string, number>,
    expirations: Record<string, string> 
  } {
    const keys = Object.keys(this.cache);
    const sizes: Record<string, number> = {};
    const expirations: Record<string, string> = {};
    
    keys.forEach(key => {
      const entry = this.cache[key];
      // Estimate size in bytes (rough approximation)
      sizes[key] = JSON.stringify(entry.data).length;
      
      if (entry.expiry === null) {
        expirations[key] = 'never';
      } else {
        const remaining = entry.timestamp + entry.expiry - Date.now();
        expirations[key] = remaining > 0 ? this.formatTime(remaining) : 'expired';
      }
    });
    
    return {
      entries: keys.length,
      keys,
      sizes,
      expirations
    };
  }
}

// Create a singleton instance
const apiCache = new ApiCache();
export default apiCache;
